import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, XCircle, CalendarDays } from 'lucide-react';
import { Container } from '@vnetwork/ui/layout';
import {
  Button, Card, CardBody, CardHeader, CardTitle, Chip, Skeleton,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter,
  Input,
} from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import { DetailHeader } from '@/components/detail-header';
import { InfoGrid } from '@/pages/account/components/InfoGrid';
import { useAdminDayOffDetail } from '@/api/dayoffs-admin';
import { dataCentralApi } from '@/api/client';
import { formatDate } from '@/lib/format';
import { statusTone } from '@/pages/account/components/status-tone';

const typeLabel = (s: string) =>
  s ? s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : '—';

const dayOffOptionLabel = (option: number | string | undefined): string => {
  const o = Number(option ?? 2);
  if (o === 0) return 'Morning';
  if (o === 1) return 'Afternoon';
  return 'All day';
};

function errMsg(err: unknown): string {
  const r = (err as { response?: { data?: { error_description?: string; message?: string } } })?.response;
  return r?.data?.error_description ?? r?.data?.message ?? 'Something went wrong.';
}

/** USER must trigger approve/reject writes. This page wires the UI + calls. */
export default function DayoffsAdminDetailPage() {
  const { id } = useParams<{ id: string }>();
  const dayOffId = id ? parseInt(id, 10) : undefined;
  const toast = useToast();

  const { data, isLoading, isError, refetch } = useAdminDayOffDetail(dayOffId);

  const [approveTarget, setApproveTarget] = useState<'leader' | 'bod' | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionPending, setActionPending] = useState(false);

  async function handleApprove() {
    if (!approveTarget || !dayOffId) return;
    setActionPending(true);
    try {
      if (approveTarget === 'leader') {
        await dataCentralApi.put(`/employees/day_offs/${dayOffId}/lead/approve`, {
          day_off_feedback_lead: '',
        });
        toast.success('Leader-approved');
      } else {
        await dataCentralApi.put(`/employees/day_offs/${dayOffId}/bod/approve`, {
          day_off_feedback_bod: '',
        });
        toast.success('BOD-approved');
      }
      setApproveTarget(null);
      refetch();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setActionPending(false);
    }
  }

  async function handleReject() {
    if (!rejectReason.trim() || !dayOffId) return;
    setActionPending(true);
    try {
      await dataCentralApi.put(`/employees/day_offs/${dayOffId}/reject`, {
        day_off_feedback: rejectReason.trim(),
      });
      toast.success('Day-off rejected');
      setRejectOpen(false);
      setRejectReason('');
      refetch();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setActionPending(false);
    }
  }

  if (isLoading) {
    return (
      <Container size="full">
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Skeleton style={{ height: 56 }} />
          <Skeleton style={{ height: 200 }} />
        </div>
      </Container>
    );
  }

  if (isError || !data) {
    return (
      <Container size="full">
        <Alert
          tone="danger"
          title="Failed to load day-off"
          actions={<Button size="sm" variant="ghost" onClick={() => refetch()}>Retry</Button>}
        />
      </Container>
    );
  }

  const canApprove =
    data.day_off_status === 'pending' || data.day_off_status === 'leader-approved';
  const canReject =
    data.day_off_status === 'pending' || data.day_off_status === 'leader-approved';

  return (
    <Container size="full">
      <DetailHeader
        parent={{ label: 'Day-offs', to: '/dayoffs' }}
        current={`#${data.day_off_id}`}
        idPrefix={`#${data.day_off_id}`}
        title={data.employee_name}
        meta={
          <StatusPill tone={statusTone(data.day_off_status)}>
            {data.day_off_status}
          </StatusPill>
        }
        actions={
          <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
            {canApprove && (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  startIcon={<CheckCircle2 size={14} />}
                  onClick={() => setApproveTarget('leader')}
                >
                  Leader approve
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  startIcon={<CheckCircle2 size={14} />}
                  onClick={() => setApproveTarget('bod')}
                >
                  BOD approve
                </Button>
              </>
            )}
            {canReject && (
              <Button
                variant="danger"
                size="sm"
                startIcon={<XCircle size={14} />}
                onClick={() => setRejectOpen(true)}
              >
                Reject
              </Button>
            )}
          </div>
        }
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)', marginTop: 'var(--vn-space-4)' }}>
        {/* Overview */}
        <Card>
          <CardHeader>
            <CardTitle>Leave request details</CardTitle>
          </CardHeader>
          <CardBody>
            <InfoGrid
              items={[
                { label: 'Employee', value: data.employee_name },
                { label: 'Department', value: data.department_name || '—' },
                { label: 'Position', value: data.position_name || '—' },
                { label: 'Leave type', value: typeLabel(data.day_off_type) },
                { label: 'Total days', value: String(data.number_of_day_off ?? 0) },
                { label: 'Status', value: <StatusPill tone={statusTone(data.day_off_status)}>{data.day_off_status}</StatusPill> },
                { label: 'Created', value: formatDate(data.day_off_created_at) },
                { label: 'Updated', value: formatDate(data.day_off_updated_at) },
                { label: 'Reason', value: data.day_off_reason || '—', full: true },
                { label: 'Note', value: data.day_off_note || '—', full: true },
              ]}
            />
          </CardBody>
        </Card>

        {/* Date range */}
        <Card>
          <CardHeader>
            <CardTitle>Leave dates</CardTitle>
          </CardHeader>
          <CardBody>
            {data.day_off_range_time.length === 0 ? (
              <span style={{ color: 'var(--vn-fg-muted)' }}>No dates recorded</span>
            ) : (
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                {data.day_off_range_time.map((r, i) => {
                  const date = r.start_day_off || r.startdayoff || '';
                  const option = r.day_off_option ?? r.dayoff_option;
                  return (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--vn-space-3)',
                        padding: 'var(--vn-space-2) var(--vn-space-3)',
                        background: 'var(--vn-bg-subtle)',
                        borderRadius: 'var(--vn-radius-md)',
                      }}
                    >
                      <CalendarDays size={16} style={{ color: 'var(--vn-fg-muted)', flexShrink: 0 }} />
                      <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>
                        {date ? formatDate(date) : '—'}
                      </span>
                      <Chip size="sm" tone="neutral">{dayOffOptionLabel(option)}</Chip>
                    </div>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Approval trail */}
        <Card>
          <CardHeader>
            <CardTitle>Approval trail</CardTitle>
          </CardHeader>
          <CardBody>
            <InfoGrid
              items={[
                {
                  label: 'Leader approval',
                  value: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                      <Chip size="sm" tone={data.day_off_leader_confirmed ? 'success' : 'neutral'}>
                        {data.day_off_leader_confirmed ? 'Approved' : 'Pending'}
                      </Chip>
                      {data.day_off_leader_name ? (
                        <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                          {data.day_off_leader_name}
                          {data.day_off_leader_confirmed_at
                            ? ` · ${formatDate(data.day_off_leader_confirmed_at)}`
                            : ''}
                        </span>
                      ) : null}
                    </div>
                  ),
                },
                {
                  label: 'BOD approval',
                  value: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                      <Chip size="sm" tone={data.day_off_bod_confirmed ? 'success' : 'neutral'}>
                        {data.day_off_bod_confirmed ? 'Approved' : 'Pending'}
                      </Chip>
                      {data.day_off_bod_name ? (
                        <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                          {data.day_off_bod_name}
                          {data.day_off_bod_confirmed_at
                            ? ` · ${formatDate(data.day_off_bod_confirmed_at)}`
                            : ''}
                        </span>
                      ) : null}
                    </div>
                  ),
                },
                { label: 'Leader feedback', value: data.day_off_feedback_lead || '—' },
                { label: 'BOD feedback', value: data.day_off_feedback_bod || '—' },
              ]}
            />
          </CardBody>
        </Card>
      </div>

      {/* Approve confirm dialog */}
      <ConfirmDialog
        open={approveTarget !== null}
        onOpenChange={(o) => !o && !actionPending && setApproveTarget(null)}
        title={approveTarget === 'leader' ? 'Leader approve this leave?' : 'BOD approve this leave?'}
        description={`Approve ${data.employee_name}'s leave request (${data.number_of_day_off} day(s))? This action moves the request forward in its lifecycle.`}
        confirmLabel={approveTarget === 'leader' ? 'Leader approve' : 'BOD approve'}
        loading={actionPending}
        onConfirm={handleApprove}
      />

      {/* Reject dialog */}
      <RejectDialog
        open={rejectOpen}
        pending={actionPending}
        value={rejectReason}
        onChange={setRejectReason}
        onClose={() => { setRejectOpen(false); setRejectReason(''); }}
        onSubmit={handleReject}
        employeeName={data.employee_name}
      />
    </Container>
  );
}

function RejectDialog({
  open, pending, value, onChange, onClose, onSubmit, employeeName,
}: {
  open: boolean;
  pending: boolean;
  value: string;
  onChange: (v: string) => void;
  onClose: () => void;
  onSubmit: () => void;
  employeeName: string;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject leave request</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p style={{ color: 'var(--vn-fg-muted)', marginBottom: 'var(--vn-space-3)' }}>
            Rejecting {employeeName}'s leave request. This cannot be undone.
          </p>
          <Field label="Rejection reason" required>
            {(p) => (
              <Input
                {...p}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Explain why the leave is rejected…"
                autoFocus
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>Cancel</Button>
          <Button
            variant="danger"
            disabled={!value.trim() || pending}
            onClick={onSubmit}
          >
            Reject
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
